import React, { useState, useEffect, useCallback } from 'react';
import {
  PageHeader, StatCards, Card, TabBar, Table,
  Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES
} from '../components/UI';
import * as api from '../api/client';
import { printReservationConfirmation, printRoomInvoice } from '../utils/print';

const STATUS_COLORS = {
  pending:'#e67e22', confirmed:'#3498db','checked-in':'#2ecc71',cancelled:'#e74c3c'
};

export default function ReservationsPage({ notify }) {
  const [tab, setTab]                   = useState('pending');
  const [reservations, setReservations] = useState([]);
  const [rooms, setRooms]               = useState([]);
  const [selected, setSelected]         = useState(null);
  const [assignRoom, setAssignRoom]     = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterRoomType, setFilterRoomType] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo]   = useState('');

  const load = useCallback(async () => {
    const [r, rm] = await Promise.all([api.getReservations(), api.getRooms()]);
    setReservations(r.data.results || r.data);
    setRooms(rm.data.results || rm.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const applyFilters = (list) => {
    let out = list;
    if (filterSearch) {
      const s = filterSearch.toLowerCase();
      out = out.filter(r =>
        r.name?.toLowerCase().includes(s) || r.phone?.includes(s) ||
        r.email?.toLowerCase().includes(s) || r.confirmation_code?.toLowerCase().includes(s)
      );
    }
    if (filterRoomType) out = out.filter(r => r.room_type === filterRoomType);
    if (filterDateFrom) out = out.filter(r => r.check_in >= filterDateFrom);
    if (filterDateTo)   out = out.filter(r => r.check_out <= filterDateTo);
    return out;
  };

  const filtered  = applyFilters(reservations);
  const pending   = filtered.filter(r => r.status === 'pending');
  const confirmed = filtered.filter(r => r.status === 'confirmed');
  const checkedIn = filtered.filter(r => r.status === 'checked-in');
  const cancelled = filtered.filter(r => r.status === 'cancelled');

  const confirm = async (res) => {
    try {
      await api.confirmReservation(res.id, { room: assignRoom || undefined });
      await load(); setSelected(null); setAssignRoom('');
      notify(`Reservation ${res.confirmation_code} confirmed!`);
    } catch(e) { notify(e.response?.data?.error || 'Error confirming', 'error'); }
  };

  const doCheckin = async (res) => {
    try {
      const result = await api.checkinReservation(res.id, { room: assignRoom || undefined });
      await load(); setSelected(null); setAssignRoom('');
      notify(`${res.name} checked in!`);
      if (result.data?.guest) printRoomInvoice(result.data.guest);
    } catch(e) { notify(e.response?.data?.error || 'Error checking in', 'error'); }
  };

  const cancel = async (res) => {
    if (!window.confirm(`Cancel reservation ${res.confirmation_code}?`)) return;
    await api.cancelReservation(res.id);
    await load();
    notify('Reservation cancelled');
  };

  const avail = rooms.filter(r => r.status === 'available');

  const depositAmt = selected
    ? (selected.deposit_amount
        ? parseFloat(selected.deposit_amount)
        : Math.round(parseFloat(selected.estimated_total || 0) * 0.3))
    : 0;

  const ReservationRow = (res) => [
    <div>
      <div style={{fontWeight:700,color:'#c8a96e',fontSize:13,fontFamily:'monospace'}}>{res.confirmation_code}</div>
      <div style={{fontSize:10,color:'var(--textFaint)'}}>{new Date(res.created_at).toLocaleDateString('en-KE')}</div>
    </div>,
    <div>
      <div style={{fontWeight:600,color:'var(--textPrimary)'}}>{res.name}</div>
      <div style={{fontSize:11,color:'var(--textFaint)'}}>{res.phone}</div>
    </div>,
    <div>
      <div>{res.room_type}</div>
      {res.needs_transport && <div style={{fontSize:10,color:'#3498db'}}>🚐 Transport</div>}
    </div>,
    `${res.check_in} → ${res.check_out}`,
    `${res.nights}n · ${res.adults}A ${res.children}C`,
    <span style={{color:'#c8a96e',fontWeight:700}}>{fmtKES(res.estimated_total)}</span>,
    <span style={{color:STATUS_COLORS[res.status]||'#aaa',fontWeight:600,fontSize:12,textTransform:'uppercase'}}>{res.status}</span>,
    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
      <button style={{...btnSm,background:'#1a2a3a'}} onClick={()=>{setSelected(res);setAssignRoom(res.room||'');}}>View</button>
      <button style={{...btnSm,background:'#2a1a0a'}} onClick={()=>printReservationConfirmation(res)}>🖨</button>
      {res.status!=='cancelled'&&res.status!=='checked-in'&&
        <button style={{...btnSm,background:'#3a1010'}} onClick={()=>cancel(res)}>✕</button>}
    </div>
  ];

  const tabs = [
    {id:'pending',   label:`Pending (${pending.length})`,    icon:'⏳'},
    {id:'confirmed', label:`Confirmed (${confirmed.length})`, icon:'✅'},
    {id:'checked-in',label:`Checked-In (${checkedIn.length})`,icon:'🏨'},
    {id:'cancelled', label:`Cancelled (${cancelled.length})`, icon:'✕'},
    {id:'all',       label:'All',                             icon:'📋'},
  ];
  const displayed =
    tab==='pending'   ? pending   : tab==='confirmed' ? confirmed :
    tab==='checked-in'? checkedIn : tab==='cancelled'  ? cancelled : filtered;

  return (
    <div>
      <PageHeader icon="📅" title="Reservations" sub="Online bookings and future reservations"/>
      <StatCards stats={[
        {label:'Pending Review', value:pending.length,   color:'#e67e22'},
        {label:'Confirmed',      value:confirmed.length, color:'#3498db'},
        {label:'Checked In',     value:checkedIn.length, color:'#2ecc71'},
        {label:'Total',          value:reservations.length, color:'#c8a96e'},
      ]}/>

      {/* ── Filter bar ── */}
      <Card style={{marginBottom:12}}>
        <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end'}}>
          <div style={{flex:'1 1 180px'}}>
            <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>🔍 Search</div>
            <input style={{...inputStyle,margin:0}} placeholder="Name, phone, email, ref code…"
              value={filterSearch} onChange={e=>setFilterSearch(e.target.value)}/>
          </div>
          <div style={{flex:'1 1 130px'}}>
            <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>Room Type</div>
            <select style={{...inputStyle,margin:0}} value={filterRoomType} onChange={e=>setFilterRoomType(e.target.value)}>
              <option value="">All Types</option>
              <option>Standard</option><option>Deluxe</option><option>Suite</option><option>Presidential</option>
            </select>
          </div>
          <div style={{flex:'1 1 130px'}}>
            <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>Check-In From</div>
            <input type="date" style={{...inputStyle,margin:0}} value={filterDateFrom} onChange={e=>setFilterDateFrom(e.target.value)}/>
          </div>
          <div style={{flex:'1 1 130px'}}>
            <div style={{fontSize:10,color:'var(--textFaint)',marginBottom:4}}>Check-Out To</div>
            <input type="date" style={{...inputStyle,margin:0}} value={filterDateTo} onChange={e=>setFilterDateTo(e.target.value)}/>
          </div>
          <button style={{...btnSm,background:'#2a2a2a',marginBottom:1}}
            onClick={()=>{setFilterSearch('');setFilterRoomType('');setFilterDateFrom('');setFilterDateTo('');}}>
            Clear
          </button>
        </div>
      </Card>

      <TabBar tabs={tabs} active={tab} onChange={setTab}/>
      <Card title={`${tab.charAt(0).toUpperCase()+tab.slice(1)} Reservations (${displayed.length})`}>
        {displayed.length===0 ? <Empty text="No reservations in this category"/> :
          <Table headers={['Ref','Guest','Room','Dates','Guests','Total','Status','Actions']} rows={displayed.map(ReservationRow)}/>}
      </Card>

      {/* ── Detail Modal ── */}
      {selected && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'#111',border:'1px solid #222',borderRadius:10,padding:24,maxWidth:620,width:'100%',maxHeight:'92vh',overflowY:'auto',boxShadow:'0 24px 80px #000'}}>

            {/* Header */}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
              <div>
                <div style={{fontSize:10,color:'#c8a96e',letterSpacing:3,textTransform:'uppercase'}}>Reservation</div>
                <div style={{fontSize:24,fontWeight:800,color:'#c8a96e',fontFamily:'monospace'}}>{selected.confirmation_code}</div>
                <span style={{
                  display:'inline-block',marginTop:4,padding:'3px 12px',
                  background:(STATUS_COLORS[selected.status]||'#aaa')+'22',
                  border:`1px solid ${STATUS_COLORS[selected.status]||'#aaa'}55`,
                  borderRadius:20,fontSize:11,fontWeight:700,
                  color:STATUS_COLORS[selected.status]||'#aaa',textTransform:'uppercase'
                }}>{selected.status}</span>
              </div>
              <button onClick={()=>setSelected(null)} style={{background:'none',border:'none',color:'var(--textFaint)',fontSize:22,cursor:'pointer'}}>✕</button>
            </div>

            {/* Deposit banner — show for pending */}
            {selected.status==='pending' && (
              <div style={{padding:'12px 14px',background:'#1a1000',border:'2px solid #c8a96e44',borderRadius:6,marginBottom:14}}>
                <div style={{fontSize:12,fontWeight:800,color:'#c8a96e',marginBottom:6}}>⚠ Reservation Deposit Required</div>
                <div style={{fontSize:12,color:'var(--textMuted)',lineHeight:1.7}}>
                  A deposit of <strong style={{color:'#c8a96e',fontSize:15}}>{fmtKES(depositAmt)}</strong> must be
                  received before confirming this reservation.<br/>
                  <span style={{fontSize:11}}>Balance of <strong>{fmtKES(Math.max(0,parseFloat(selected.estimated_total||0)-depositAmt))}</strong> is due at check-in. Failure to pay deposit within 48 hours may result in cancellation.</span>
                </div>
              </div>
            )}

            {/* Guest info grid */}
            <div style={{background:'#0a0a0a',border:'1px solid #1e1e1e',borderRadius:6,padding:14,marginBottom:14}}>
              <div style={{fontSize:10,color:'#c8a96e',textTransform:'uppercase',letterSpacing:2,marginBottom:10}}>Guest Details</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))',gap:'10px 20px'}}>
                {[
                  ['Name',           selected.name],
                  ['Email',          selected.email],
                  ['Phone',          selected.phone],
                  ['ID / Passport',  selected.id_number||'—'],
                  ['Room Type',      selected.room_type],
                  ['Assigned Room',  selected.room_number||'Not assigned'],
                  ['Check-In',       selected.check_in],
                  ['Check-Out',      selected.check_out],
                  ['Nights',         selected.nights],
                  ['Adults / Children',`${selected.adults} / ${selected.children}`],
                  ['Payment Mode',   (selected.payment_mode||'cash').toUpperCase()],
                ].map(([l,v])=>(
                  <div key={l}>
                    <div style={{fontSize:10,color:'var(--textFaint)',textTransform:'uppercase',letterSpacing:1}}>{l}</div>
                    <div style={{fontSize:13,color:'var(--textSecond)',fontWeight:500,marginTop:2}}>{v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Financials */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:10,marginBottom:14}}>
              <div style={{background:'#0f0f0f',border:'1px solid #c8a96e33',borderRadius:6,padding:12}}>
                <div style={{fontSize:10,color:'var(--textFaint)',textTransform:'uppercase',letterSpacing:1}}>Est. Total</div>
                <div style={{fontSize:20,fontWeight:800,color:'#c8a96e'}}>{fmtKES(selected.estimated_total)}</div>
              </div>
              <div style={{background:'#0f0f0f',border:'1px solid #e67e2233',borderRadius:6,padding:12}}>
                <div style={{fontSize:10,color:'var(--textFaint)',textTransform:'uppercase',letterSpacing:1}}>Deposit</div>
                <div style={{fontSize:20,fontWeight:800,color:'#e67e22'}}>{fmtKES(depositAmt)}</div>
                <div style={{fontSize:10,color:selected.deposit_paid?'#2ecc71':'#e74c3c',marginTop:2}}>
                  {selected.deposit_paid?'✓ Paid':'⚠ Unpaid'}
                </div>
              </div>
              {selected.needs_transport && (
                <div style={{background:'#0f0f0f',border:'1px solid #3498db33',borderRadius:6,padding:12}}>
                  <div style={{fontSize:10,color:'var(--textFaint)',textTransform:'uppercase',letterSpacing:1}}>🚐 Transport Requested</div>
                  <div style={{fontSize:20,fontWeight:800,color:'#3498db'}}>{fmtKES(selected.transport_fee||0)}</div>
                  <div style={{fontSize:11,color:'#3498db',marginTop:4}}>📍 {selected.transport_pickup||'Pickup location TBC'}</div>
                  {selected.transport_fee===0||!selected.transport_fee ? <div style={{fontSize:10,color:'var(--textFaint)',marginTop:2}}>Fee to be confirmed by hotel team</div> : null}
                </div>
              )}
            </div>

            {/* Special requests */}
            {selected.special_requests && (
              <div style={{background:'var(--bgInput)',border:'1px solid #2a2a2a',borderRadius:4,padding:10,marginBottom:14,fontSize:12,color:'var(--textMuted)'}}>
                <div style={{fontSize:10,color:'#c8a96e',textTransform:'uppercase',letterSpacing:1,marginBottom:4}}>Special Requests</div>
                {selected.special_requests}
              </div>
            )}

            {/* Room assign */}
            {(selected.status==='pending'||selected.status==='confirmed') && (
              <Field label="Assign Room (optional)">
                <select style={inputStyle} value={assignRoom} onChange={e=>setAssignRoom(e.target.value)}>
                  <option value="">— Select Room —</option>
                  {avail.map(r=>(
                    <option key={r.id} value={r.id}>Room {r.number} — {r.type} ({fmtKES(r.price)}/night)</option>
                  ))}
                </select>
              </Field>
            )}

            {/* Actions */}
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:18,paddingTop:14,borderTop:'1px solid #1e1e1e'}}>
              <button style={{...btnSm,background:'#1a2a1a'}} onClick={()=>printReservationConfirmation(selected)}>
                🖨 Print Confirmation
              </button>
              {selected.status==='pending' && (
                <button style={btnPrimary} onClick={()=>confirm(selected)}>✓ Confirm Reservation</button>
              )}
              {(selected.status==='pending'||selected.status==='confirmed') && (
                <button style={{...btnPrimary,background:'#27ae60'}} onClick={()=>doCheckin(selected)}>
                  🏨 Check In Now
                </button>
              )}
              {selected.status!=='cancelled'&&selected.status!=='checked-in' && (
                <button style={{...btnSm,background:'#3a1010',color:'#e74c3c'}}
                  onClick={()=>{cancel(selected);setSelected(null);}}>✕ Cancel</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
